import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'localization';
import type { SphereAttachment } from 'types/attachment';
import { formatSize } from '../../../utils/formatBytes';
import { getIconClass } from '../../../utils/attachmentIcon';
import './AttachmentRowAtom.scss';

export interface AttachmentRowAtomProps {
  attachment: SphereAttachment;
  uploadedByName?: string;
  canDelete?: boolean;
  onDelete?: (attachment: SphereAttachment) => void;
  onClick?: (attachment: SphereAttachment) => void;
  className?: string;
}

function formatRelative(iso: string, locale: string): string {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return '';
  const diffSec = Math.round((target - Date.now()) / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHours = Math.round(diffMin / 60);
  const diffDays = Math.round(diffHours / 24);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (Math.abs(diffDays) >= 1) return rtf.format(diffDays, 'day');
  if (Math.abs(diffHours) >= 1) return rtf.format(diffHours, 'hour');
  if (Math.abs(diffMin) >= 1) return rtf.format(diffMin, 'minute');
  return rtf.format(diffSec, 'second');
}

const AttachmentRowAtom: React.FC<AttachmentRowAtomProps> = ({
  attachment,
  uploadedByName,
  canDelete = false,
  onDelete,
  onClick,
  className = '',
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || 'en-US';

  const scanStatus = attachment.scan_status;
  const isScanPending = scanStatus === 'pending';
  const isRemoved = scanStatus === 'malicious' || scanStatus === 'quarantined';

  const isLink = attachment.attachment_type === 'link';

  // Default click handler for link rows when no parent-supplied onClick exists.
  // Files require a parent-supplied handler because file open/preview needs
  // signed-URL plumbing the atom shouldn't own; link URLs are opaque to the
  // app and safe to open natively.
  const effectiveOnClick = useMemo<
    ((attachment: SphereAttachment) => void) | undefined
  >(() => {
    if (typeof onClick === 'function') return onClick;
    if (isLink && attachment.url) {
      const url = attachment.url;
      return () => {
        window.open(url, '_blank', 'noopener,noreferrer');
      };
    }
    return undefined;
  }, [onClick, isLink, attachment.url]);

  const isClickable =
    typeof effectiveOnClick === 'function' && !isScanPending && !isRemoved;
  const displayName = attachment.name?.trim() || t('attachments.unnamed');
  const iconClass = getIconClass(attachment);
  let subtitle: string | null | undefined;
  if (isRemoved) {
    subtitle = t('attachments.removedMalicious');
  } else if (isLink) {
    subtitle = attachment.url;
  } else {
    subtitle = formatSize(attachment.size_bytes);
  }
  const relative = formatRelative(attachment.uploaded_at, locale);
  const userLabel = t('attachments.byUser', { user: uploadedByName ?? attachment.uploaded_by });
  const typeLabel = isLink ? t('attachments.types.link') : t('attachments.types.file');

  const handleClick = useCallback(() => {
    if (isClickable) effectiveOnClick?.(attachment);
  }, [attachment, isClickable, effectiveOnClick]);

  // Native <button> already maps Enter/Space to click, so we no longer
  // need a custom handler for the clickable interactive region.

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDelete?.(attachment);
    },
    [attachment, onDelete],
  );

  const rootClasses = [
    'attachment-row-atom',
    isClickable ? 'attachment-row-atom--clickable' : '',
    isLink ? 'attachment-row-atom--link' : 'attachment-row-atom--file',
    isScanPending ? 'attachment-row-atom--scanning' : '',
    isRemoved ? 'attachment-row-atom--removed' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  let rowTitle: string | undefined;
  if (isScanPending) {
    rowTitle = t('attachments.scanPending');
  } else if (isRemoved) {
    rowTitle = t('attachments.removedMalicious');
  } else {
    rowTitle = undefined;
  }

  // Delete is hidden for rows that have been flagged/quarantined (the row is
  // effectively a tombstone); it remains available for scan-pending rows so a
  // user can cancel a still-running upload.
  const showDelete = canDelete && typeof onDelete === 'function' && !isRemoved;

  const interactiveContent = (
    <>
      <div className="attachment-row-atom__icon" aria-hidden="true">
        <i className={iconClass} />
      </div>
      <div className="attachment-row-atom__body">
        <div className="attachment-row-atom__name-line">
          <span className="attachment-row-atom__name" title={displayName}>
            {displayName}
          </span>
          {isScanPending && (
            <output
              className="attachment-row-atom__badge attachment-row-atom__badge--scanning"
              aria-label={t('attachments.scanPending')}
            >
              <i className="pi pi-spin pi-spinner" aria-hidden="true" />
              <span>{t('attachments.scanning')}</span>
            </output>
          )}
          {isRemoved && (
            <output
              className="attachment-row-atom__badge attachment-row-atom__badge--removed"
            >
              <i className="pi pi-shield" aria-hidden="true" />
              <span>{t('attachments.removedMalicious')}</span>
            </output>
          )}
        </div>
        {subtitle && (
          <div className="attachment-row-atom__subtitle" title={subtitle}>
            {subtitle}
          </div>
        )}
        <div className="attachment-row-atom__meta">
          <span className="attachment-row-atom__user">{userLabel}</span>
          {relative && (
            <>
              <span className="attachment-row-atom__separator" aria-hidden="true">·</span>
              <time className="attachment-row-atom__time" dateTime={attachment.uploaded_at}>
                {relative}
              </time>
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div
      className={rootClasses}
      title={rowTitle}
      data-attachment-id={attachment.id}
      data-scan-status={scanStatus}
    >
      {isClickable ? (
        <button
          type="button"
          className="attachment-row-atom__interactive"
          onClick={handleClick}
          aria-label={`${typeLabel}: ${displayName}`}
          aria-busy={isScanPending || undefined}
          aria-disabled={isScanPending || isRemoved || undefined}
        >
          {interactiveContent}
        </button>
      ) : (
        <div
          className="attachment-row-atom__interactive attachment-row-atom__interactive--static"
          aria-label={`${typeLabel}: ${displayName}`}
          aria-busy={isScanPending || undefined}
        >
          {interactiveContent}
        </div>
      )}
      {showDelete && (
        <button
          type="button"
          className="attachment-row-atom__delete"
          onClick={handleDeleteClick}
          aria-label={t('attachments.delete')}
        >
          <i className="pi pi-trash" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default AttachmentRowAtom;
